import { FocusEvent, forwardRef, SyntheticEvent, useRef, useState } from 'react';
import { NumericFormat, NumericFormatProps } from 'react-number-format';

interface ICustomProps {
  name: string;
  onChange: (event: {
    target: { name: string; value: string };
    // source is always 'event' from this adapter — react-number-format's prop-change
    // echoes (source: 'prop') are filtered out so internal reformats never leak into
    // form state. See the onValueChange interceptor below.
    source: 'prop' | 'event';
    event?: SyntheticEvent;
  }) => void;
}

// Strips trailing zeros from the decimal part of a numericString (e.g. "1.2300" →
// "1.23"). Used only for the display value passed to NumericFormat so the input can
// shrink back after a blur-padding; the underlying form value is never modified.
const stripTrailingDecimalZeros = (value: string) => {
  if (!value.includes('.')) {
    return value;
  }

  return value.replace(/0+$/, '').replace(/\.$/, '');
};

// `fixedDecimalScale` is focus-aware here (diverges from react-number-format): it
// applies only when blurred, so users can type `.215` without the caret locking.
const NumericFormatAdapter = forwardRef(function NumericFormatAdapter(
  {
    name,
    onChange,
    allowNegative = false,
    isAllowed,
    min,
    max,
    fixedDecimalScale,
    onFocus,
    onBlur,
    ...rest
  }: NumericFormatProps & ICustomProps,
  ref
) {
  const [isFocused, setIsFocused] = useState(false);
  // Tracks whether the user has typed in the input since the most recent focus.
  // The display-only strip only runs on the first render after focus — once the
  // user types, the prop value reflects their input (including intentional
  // trailing zeros like ".20" → ".205") and stripping it would eat their digits.
  const hasTypedSinceFocus = useRef(false);

  const effectiveFixedDecimalScale = isFocused ? false : fixedDecimalScale;

  const handleIsAllowed = (values: any) => {
    if (isAllowed) {
      return isAllowed(values);
    }

    if (values.floatValue === undefined) {
      return true;
    }

    if (min !== undefined && values.floatValue < min) {
      return false;
    }

    if (max !== undefined && values.floatValue > max) {
      return false;
    }

    return true;
  };

  const handleFocus = (event: FocusEvent<HTMLInputElement>) => {
    hasTypedSinceFocus.current = false;
    setIsFocused(true);
    onFocus?.(event);
  };

  const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    onBlur?.(event);
  };

  // Display-only strip: feed NumericFormat a stripped value when the user first
  // re-enters a field that holds a blur-padded value (e.g. "1.2300" → "1.23").
  // The form state stays at the padded value — only the rendered display changes.
  const shouldStripForDisplay =
    fixedDecimalScale && isFocused && !hasTypedSinceFocus.current && typeof rest.value === 'string';

  const displayValue = shouldStripForDisplay
    ? stripTrailingDecimalZeros(rest.value as string)
    : rest.value;

  return (
    <NumericFormat
      {...rest}
      name={name}
      value={displayValue || (0 === rest.value ? 0 : '')}
      onValueChange={(values, { source, event }) => {
        // react-number-format echoes prop-derived reformats back via onValueChange
        // with source: 'prop'. They carry no new user data — RHF already holds the
        // value. Dropping them here keeps the displayValue strip from leaking into
        // form state (no spurious dirty flag, no recalc on focus).
        if (source === 'prop') {
          return;
        }

        hasTypedSinceFocus.current = true;

        onChange({
          target: { name, value: values.value },
          source,
          event,
        });
      }}
      getInputRef={ref}
      allowNegative={allowNegative}
      isAllowed={handleIsAllowed}
      fixedDecimalScale={effectiveFixedDecimalScale}
      onFocus={handleFocus}
      onBlur={handleBlur}
    />
  );
});

export default NumericFormatAdapter;
